import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, getQueryFn } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { Zap, Plus, Send, Square, RefreshCw, ExternalLink, ChevronRight, Clock, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface AgentTask {
  id: string;
  title?: string;
  status: "running" | "stopped" | "waiting" | "error";
  task_type: string;
  created_at: number;
  updated_at: number;
  credit_usage?: number;
  task_url?: string;
}

interface AgentMessage {
  id: string;
  type: string;
  timestamp: number;
  user_message?: { content: string };
  assistant_message?: { content: string };
  error_message?: { content: string };
  status_update?: { status: string };
}

const STATUS_COLORS: Record<string, string> = {
  running: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  waiting: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  stopped: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  error: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
};

function TaskStatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[status] ?? STATUS_COLORS.stopped}`}>
      {status === "running" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
      {status}
    </span>
  );
}

function MessageBubble({ msg }: { msg: AgentMessage }) {
  if (msg.type === "user_message" && msg.user_message) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2 text-sm whitespace-pre-wrap">
          {msg.user_message.content}
        </div>
      </div>
    );
  }
  if (msg.type === "assistant_message" && msg.assistant_message) {
    return (
      <div className="flex justify-start">
        <div className="max-w-[80%] bg-muted rounded-2xl rounded-tl-sm px-4 py-2 text-sm whitespace-pre-wrap">
          {msg.assistant_message.content}
        </div>
      </div>
    );
  }
  if (msg.type === "error_message" && msg.error_message) {
    return (
      <div className="flex justify-start">
        <div className="max-w-[80%] bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-2xl px-4 py-2 text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap">
          {msg.error_message.content}
        </div>
      </div>
    );
  }
  if (msg.type === "status_update" && msg.status_update) {
    return (
      <div className="flex justify-center">
        <span className="text-xs text-muted-foreground px-3 py-1 bg-muted/50 rounded-full">
          Status: {msg.status_update.status}
        </span>
      </div>
    );
  }
  return null;
}

function TaskThread({ task, onBack }: { task: AgentTask; onBack: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: taskData, error: taskError } = useQuery<any>({
    queryKey: ["/api/manus/tasks", task.id],
    queryFn: () => apiRequest("GET", `/api/manus/tasks/${task.id}`).then(r => r.json()),
    // Stop polling permanently once the task 404s (deleted / not owned).
    refetchInterval: (query) =>
      (query.state.error as any)?.status !== 404 &&
      (task.status === "running" || task.status === "waiting")
        ? 5000
        : false,
    // Never retry a 404 — a stale/deleted task will never come back.
    retry: (failureCount, err: any) => err?.status !== 404 && failureCount < 2,
  });

  const notFound = (taskError as any)?.status === 404;

  const { data: messagesData, isLoading: loadingMessages } = useQuery<any>({
    queryKey: ["/api/manus/tasks", task.id, "messages"],
    queryFn: () => apiRequest("GET", `/api/manus/tasks/${task.id}/messages?order=asc&limit=100`).then(r => r.json()),
    refetchInterval: !notFound && (task.status === "running" || task.status === "waiting") ? 3000 : false,
    retry: (failureCount, err: any) => err?.status !== 404 && failureCount < 2,
    enabled: !notFound,
  });

  const currentTask: AgentTask = taskData?.task ?? task;
  const messages: AgentMessage[] = messagesData?.items ?? messagesData?.data ?? [];

  const sendMutation = useMutation({
    mutationFn: (prompt: string) =>
      apiRequest("POST", `/api/manus/tasks/${task.id}/messages`, { prompt }).then(r => r.json()),
    onSuccess: () => {
      setInput("");
      queryClient.invalidateQueries({ queryKey: ["/api/manus/tasks", task.id, "messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/manus/tasks", task.id] });
    },
    onError: (e: any) => toast({ title: "Failed to send", description: e.message, variant: "destructive" }),
  });

  const stopMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/manus/tasks/${task.id}/stop`).then(r => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/manus/tasks", task.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/manus/tasks"] });
      toast({ title: "Task stopped" });
    },
    onError: (e: any) => toast({ title: "Failed to stop", description: e.message, variant: "destructive" }),
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const canSend = (currentTask.status === "running" || currentTask.status === "waiting") && input.trim().length > 0;

  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 p-8 text-center" data-testid="agent-task-not-found">
        <Zap className="w-10 h-10 text-muted-foreground/40" />
        <div className="space-y-1">
          <p className="font-medium">Task not found</p>
          <p className="text-sm text-muted-foreground">
            This task may have been deleted or the link is no longer valid.
          </p>
        </div>
        <Button variant="outline" onClick={onBack} data-testid="button-task-not-found-back">
          Back to tasks
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b shrink-0">
        <Button variant="ghost" size="icon" onClick={onBack} className="shrink-0" data-testid="button-task-back">
          <ChevronRight className="w-4 h-4 rotate-180" />
        </Button>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate text-sm">{currentTask.title || `Task ${currentTask.id.slice(0, 8)}`}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <TaskStatusBadge status={currentTask.status} />
            {currentTask.credit_usage !== undefined && (
              <span className="text-xs text-muted-foreground">{currentTask.credit_usage} credits</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {currentTask.task_url && (
            <Button variant="ghost" size="icon" asChild>
              <a href={currentTask.task_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          )}
          {(currentTask.status === "running" || currentTask.status === "waiting") && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => stopMutation.mutate()}
              disabled={stopMutation.isPending}
            >
              <Square className="w-3 h-3 mr-1" />
              Stop
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1">
        <div ref={scrollRef} className="p-4 space-y-3">
          {loadingMessages ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">No messages yet.</p>
          ) : (
            messages.map(msg => <MessageBubble key={msg.id} msg={msg} />)
          )}
          {currentTask.status === "running" && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-2xl rounded-tl-sm px-4 py-2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      {(currentTask.status === "running" || currentTask.status === "waiting") && (
        <div className="border-t p-3 shrink-0">
          <div className="flex gap-2">
            <Textarea
              placeholder="Send a follow-up…"
              value={input}
              onChange={e => setInput(e.target.value)}
              className="min-h-[60px] resize-none text-sm"
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (canSend) sendMutation.mutate(input.trim());
                }
              }}
            />
            <Button
              size="icon"
              disabled={!canSend || sendMutation.isPending}
              onClick={() => sendMutation.mutate(input.trim())}
            >
              {sendMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Enter to send · Shift+Enter for new line</p>
        </div>
      )}
    </div>
  );
}

export default function ZeusAgentTasks() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedTask, setSelectedTask] = useState<AgentTask | null>(null);
  const [newPrompt, setNewPrompt] = useState("");
  const [showNew, setShowNew] = useState(false);

  const { data: tasksData, isLoading, refetch } = useQuery<any>({
    queryKey: ["/api/manus/tasks"],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  const tasks: AgentTask[] = tasksData?.items ?? tasksData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (prompt: string) =>
      apiRequest("POST", "/api/manus/tasks", { prompt }).then(r => r.json()),
    onSuccess: (data) => {
      const task = data?.task;
      setNewPrompt("");
      setShowNew(false);
      queryClient.invalidateQueries({ queryKey: ["/api/manus/tasks"] });
      if (task) setSelectedTask(task);
      toast({ title: "Agent task started", description: "Your task is running." });
    },
    onError: (e: any) => toast({ title: "Failed to create task", description: e.message, variant: "destructive" }),
  });

  if (selectedTask) {
    return (
      <TaskThread
        task={selectedTask}
        onBack={() => {
          setSelectedTask(null);
          queryClient.invalidateQueries({ queryKey: ["/api/manus/tasks"] });
        }}
      />
    );
  }

  return (
    <div className="w-full p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Agent Tasks</h2>
          <p className="text-sm text-muted-foreground">
            Delegate one-off tasks to your Zeus agent
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="icon" onClick={() => refetch()} data-testid="button-refresh-tasks">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowNew(v => !v)} data-testid="button-new-agent-task">
            <Plus className="w-4 h-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>

      {/* New task form */}
      {showNew && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Delegate a task</CardTitle>
            <CardDescription>
              Describe what you want the agent to research, plan, write, or execute.
              Be as specific as you like — it can browse the web, write code, create documents, and more.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              placeholder="e.g. Research the best morning routines for productivity and create a personalised 7-day plan for me based on my goals…"
              value={newPrompt}
              onChange={e => setNewPrompt(e.target.value)}
              className="min-h-[120px] text-sm"
              autoFocus
              data-testid="input-new-task-prompt"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowNew(false); setNewPrompt(""); }}>
                Cancel
              </Button>
              <Button
                disabled={!newPrompt.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate(newPrompt.trim())}
                data-testid="button-start-task"
              >
                {createMutation.isPending
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Starting…</>
                  : <><Send className="w-4 h-4 mr-2" />Start Task</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Task list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your Tasks</CardTitle>
          <CardDescription>Click a task to view its thread and send follow-up messages.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
              <Zap className="w-10 h-10 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No tasks yet.</p>
              <Button variant="outline" onClick={() => setShowNew(true)}>
                <Plus className="w-4 h-4 mr-2" /> Delegate your first task
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {tasks.map((task) => (
                <button
                  key={task.id}
                  onClick={() => setSelectedTask(task)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
                  data-testid={`button-task-${task.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {task.title || `Task ${task.id.slice(0, 8)}`}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <TaskStatusBadge status={task.status} />
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDistanceToNow(new Date(task.created_at * 1000), { addSuffix: true })}
                      </span>
                      {task.credit_usage !== undefined && (
                        <span className="text-xs text-muted-foreground">{task.credit_usage} cr</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info card */}
      <Card className="border-yellow-200 dark:border-yellow-900 bg-yellow-50/50 dark:bg-yellow-950/20">
        <CardContent className="pt-4 pb-4">
          <div className="flex gap-3">
            <Zap className="w-5 h-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
            <div className="text-sm space-y-1">
              <p className="font-medium text-yellow-900 dark:text-yellow-200">What can the agent do?</p>
              <ul className="text-yellow-700 dark:text-yellow-300 space-y-0.5 list-disc list-inside text-xs">
                <li>Research topics and summarise findings</li>
                <li>Create personalised plans, schedules, and documents</li>
                <li>Browse the web to gather up-to-date information</li>
                <li>Write, review, and refine content</li>
                <li>Analyse data and generate insights</li>
                <li>Execute multi-step workflows autonomously</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
